import Image from 'next/future/image'
import {Button} from '@mui/material'
import {UnionToIntersection} from '@skyslope/utils'
import {NoGradientLayout} from '~/page-components'

type StatusCode = 401 | 403 | 404 | 500 | 502 | 503 | 504

export type NetworkErrorProps = {
  statusCode: StatusCode
  titleText?: string
  bodyText?: string
  allowGoBack?: boolean
}

const copy: Record<StatusCode, {titleText: string; bodyText: string}> = {
  401: {
    titleText: 'Unauthorized',
    bodyText: 'You are not authorized to view this page.',
  },
  403: {
    titleText: 'Missing permissions',
    bodyText:
      "It looks like you don't have the necessary permissions to access this page. You may need to contact support or your account administrator.",
  },
  404: {
    titleText: 'Sorry, page not found.',
    bodyText: "The content you're looking for has been removed, renamed, or is unavailable.",
  },
  500: {
    titleText: 'Internal Server Error',
    bodyText: "Sorry, we're experiencing an internal server problem. Please try again later.",
  },
  502: {
    titleText: 'Bad Gateway',
    bodyText: 'Something went wrong on our end. Please try again later.',
  },
  503: {
    titleText: 'Service Unavailable',
    bodyText: 'Something went wrong on our end. Please try again later.',
  },
  504: {
    titleText: 'Gateway Timeout',
    bodyText: 'Something went wrong on our end. Please try again later.',
  },
}

/**
 * Static network error component for handling unauthorized/other failed network calls
 * Error page copy found here: https://skyslope.atlassian.net/wiki/spaces/UXD/pages/1128792373/Error+Pages+404
 */
export function NetworkError(props: NetworkErrorProps) {
  const {statusCode, titleText, bodyText, allowGoBack} =
    props as UnionToIntersection<NetworkErrorProps>

  const finalTitle = titleText ?? copy[statusCode].titleText
  const finalBody = bodyText ?? copy[statusCode].bodyText

  return (
    <div className="NetworkError flex h-screen flex-col items-center justify-center px-8 text-center">
      <h1 className="mb-8 text-3xl font-bold">{finalTitle}</h1>
      <Image
        src={`/static/images/network-errors/${statusCode}.svg`}
        width={504}
        height={186}
        alt={statusCode.toString()}
      />
      <div className="flex max-w-lg flex-col justify-center text-center">
        <h2 className="mt-8 text-xl">{finalBody}</h2>
        {allowGoBack && (
          <div className="my-8">
            <Button variant="contained" color="primary" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export function NetworkErrorPage(props: NetworkErrorProps) {
  return (
    <NoGradientLayout className="NetworkErrorPage">
      <NetworkError {...props} />
    </NoGradientLayout>
  )
}
